import { apiClient } from '../const';

/**
 * POST /admin/bank-statement/balance
 * Permission: bank_statement.read
 * @param {Object} [params]
 * @param {boolean} [params.exclude_trades]
 * Returns: { success, data: { current_balance, total_in, total_out } }
 */
export const getBankStatementBalance = async ({ exclude_trades } = {}) => {
  const body = {};
  if (typeof exclude_trades === 'boolean') body.exclude_trades = exclude_trades;
  const res = await apiClient.post('/admin/bank-statement/balance', body, { skipBodyWrap: true });
  return res.data;
};

/**
 * GET /admin/bank-statement/types
 * Permission: bank_statement.read
 * Returns: { success, data: { types: [{ code, label }] } }
 */
export const getBankStatementTypes = async () => {
  const res = await apiClient.get('/admin/bank-statement/types');
  return res.data;
};

/**
 * POST /admin/bank-statement/list
 * Permission: bank_statement.read
 * @param {Object} params
 * @param {string} [params.from_date]        YYYY-MM-DD
 * @param {string} [params.to_date]          YYYY-MM-DD
 * @param {string} [params.direction]        'IN' | 'OUT' | ''
 * @param {string} [params.type]             Type code e.g. 'MARKETING_EXPENSES'
 * @param {boolean} [params.exclude_trades]  true | false
 * @param {number} [params.limit]            1..500 (default 50)
 * @param {number} [params.offset]           >= 0 (default 0)
 */
export const getBankStatements = async ({
  from_date = '',
  to_date = '',
  direction = '',
  type = '',
  exclude_trades,
  limit = 50,
  offset = 0,
} = {}) => {
  const body = { limit, offset };
  if (from_date) body.from_date = from_date;
  if (to_date) body.to_date = to_date;
  if (direction) body.direction = direction;
  if (type) body.type = type;
  if (typeof exclude_trades === 'boolean') body.exclude_trades = exclude_trades;
  const res = await apiClient.post('/admin/bank-statement/list', body, { skipBodyWrap: true });
  return res.data;
};

/**
 * GET /admin/bank-statement/:id
 * Permission: bank_statement.read
 * @param {number|string} id
 */
export const getBankStatementById = async (id) => {
  const res = await apiClient.get(`/admin/bank-statement/${id}`);
  return res.data;
};

/**
 * POST /admin/bank-statement
 * Permission: bank_statement.write
 * Requires Idempotency-Key header.
 * @param {Object} body
 * @param {string} body.direction     'IN' | 'OUT'
 * @param {string} body.type          Type code from /types (REQUIRED)
 * @param {number} body.amount        > 0
 * @param {string} [body.entry_date]  YYYY-MM-DD (defaults to today)
 * @param {string} [body.remarks]     max 500 chars
 * @param {string} idempotencyKey     unique per action, reused on retry
 */
export const createBankStatement = async (body, idempotencyKey) => {
  const res = await apiClient.post('/admin/bank-statement', body, {
    skipBodyWrap: true,
    headers: {
      'Idempotency-Key': idempotencyKey,
    },
  });
  return res.data;
};

/**
 * PUT /admin/bank-statement/:id
 * Permission: bank_statement.write
 * @param {number|string} id
 * @param {Object} body
 * @param {string} [body.entry_date]  YYYY-MM-DD
 * @param {string} [body.direction]   'IN' | 'OUT'
 * @param {string} [body.type]        Type code
 * @param {number} [body.amount]      > 0
 * @param {string} [body.remarks]     string
 */
export const updateBankStatement = async (id, body) => {
  const res = await apiClient.put(`/admin/bank-statement/${id}`, body, {
    skipBodyWrap: true,
  });
  return res.data;
};

/**
 * DELETE /admin/bank-statement/:id
 * Permission: bank_statement.write
 * @param {number|string} id
 */
export const deleteBankStatement = async (id) => {
  const res = await apiClient.delete(`/admin/bank-statement/${id}`);
  return res.data;
};

